// ============================================================================
// Roof Designer · StepBar — geführter Workflow in 5 Schritten
// ----------------------------------------------------------------------------
// 1 Dachfläche → 2 Maßstab → 3 Hindernisse (optional) → 4 Belegung → 5 Ergebnis
// Der aktive Schritt ist der erste unerledigte; Klick auf einen Schritt wählt
// das passende Werkzeug. Reine Ableitung aus DesignerState/Result — keine
// eigene Logik, keine Status-Mutation außer SET_ACTIVE_TOOL.
// ============================================================================
import type { DesignerState } from "../types.js";
import type { DesignerResult } from "../roof-engine.js";
import type { DesignerAction } from "../state.js";

interface Props {
  state: DesignerState;
  result: DesignerResult;
  dispatch: (a: DesignerAction) => void;
}

interface Step {
  key: string;
  label: string;
  done: boolean;
  optional?: boolean;
  tool: DesignerState["activeTool"] | null;
  hint: string;
}

export function deriveSteps(state: DesignerState, result: DesignerResult): Step[] {
  const polygonDone = state.polygon.length >= 3 && state.polygonClosed;
  const scaleDone = state.calibration.confirmed && state.calibration.metersPerPixel != null;
  const layoutDone = result.status === "ok" && result.moduleCount > 0;
  return [
    {
      key: "surface",
      label: "Dachfläche",
      done: polygonDone,
      tool: "polygon",
      hint: "Dach-Umriss zeichnen oder Gebäude-Dachfläche übernehmen",
    },
    {
      key: "scale",
      label: "Maßstab",
      done: scaleDone,
      tool: "calibrate",
      hint: "Referenzlinie mit bekannter Länge setzen (bei Import automatisch)",
    },
    {
      key: "obstacles",
      label: "Hindernisse",
      done: state.obstacles.length > 0,
      optional: true,
      tool: "obstacle",
      hint: "Kamin, Fenster, Gauben einzeichnen (optional)",
    },
    {
      key: "layout",
      label: "Belegung",
      done: layoutDone,
      tool: "select",
      hint: "Modul-Spezifikation und Abstände rechts prüfen",
    },
    {
      key: "result",
      label: "Ergebnis",
      done: polygonDone && scaleDone && layoutDone,
      tool: null,
      hint: "KPIs prüfen, Entwurf speichern/exportieren — bleibt ENTWURF",
    },
  ];
}

export function StepBar({ state, result, dispatch }: Props) {
  const steps = deriveSteps(state, result);
  // Aktiv = erster nicht erledigter Pflicht-Schritt (Hindernisse zählen nicht).
  const activeIdx = steps.findIndex((s) => !s.done && !s.optional);
  const effectiveActive = activeIdx === -1 ? steps.length - 1 : activeIdx;

  return (
    <div className="rd-steps" data-testid="rd-stepbar">
      {steps.map((s, i) => {
        const isActive = i === effectiveActive;
        const cls = [
          "rd-step",
          s.done ? "rd-step--done" : "",
          isActive ? "rd-step--active" : "",
        ].filter(Boolean).join(" ");
        return (
          <button
            key={s.key}
            className={cls}
            title={s.hint}
            onClick={() => { if (s.tool) dispatch({ type: "SET_ACTIVE_TOOL", tool: s.tool }); }}
          >
            <span className="rd-step__index">{s.done ? "✓" : i + 1}</span>
            <span className="rd-step__label">
              {s.label}
              {s.optional ? <span className="rd-step__opt"> (optional)</span> : null}
            </span>
            {i < steps.length - 1 && <span className="rd-step__arrow">›</span>}
          </button>
        );
      })}
    </div>
  );
}
